'use client';

import { GetResponseType } from '@/api/types/api';
import { ApiResponseDefinition } from '@/api/types/api-definition';
import Image from '@/components/Image';
import { getSpmPositionCode, useGlobalSpmTerminal } from '@/components/Layout/spmTerminal';
import { useTranslations } from '@/i18n/use-translations';
import { IS_US } from '@/utils/constants';
import { EPlatform } from '@/utils/enums/platform-enum';
import { env } from '@/utils/env';
import { useSpmDomAttr } from '@/reporting/spm';
import {
  FacebookOutlined,
  InstagramOutlined,
  TiktokOutlined,
  TwitterOutlined,
  YoutubeOutlined,
} from '@zeelool/icon';
import NextImage from 'next/image';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { Fragment } from 'react';
import { FOOTER_SOCIAL_POSITION_WAP } from '../../socialSpmPosition';
import DownloadApp from './DownloadApp.m';
import NavList from './NavList.m';
import SubscribeCoupon from './SubscribeCoupon';
import Subscribe_US from './Subscribe_US';
import helpCenterIcon from './icon/help-center.png';
import orderTrackingIcon from './icon/order-tracking.png';
import { shouldShowUSFooterSubscribe } from './subscribeVisibility';
import { getFooterLegalLinks } from '../2026-pc/legalLinks';
import FooterLegalItem from '../2026-pc/FooterLegalItem';
import { getFooterPaymentModules } from '../2026-pc/paymentModules';
import { getFooterCopyrightModules } from '../2026-pc/copyrightModules';

const Footer = ({
  navList,
  SDSocialMediaClick,
  isShowSubscribe,
  SDBottomNavTabClick,
  deviceType,
}: {
  navList: GetResponseType<ApiResponseDefinition['GET:/api/nav/lists']>;
  SDSocialMediaClick: (btn: string) => void;
  isShowSubscribe: boolean;
  SDBottomNavTabClick: (
    first_tab_name: string,
    second_tab_name?: string,
    first_tab_index?: number,
    second_tab_index?: number,
    spmCnt?: string,
  ) => void;
  deviceType: EPlatform;
}) => {
  const bottomSubscribeBoxTxt = (navList[0]?.content as { describe: string })?.describe || '';
  const t = useTranslations('layout.footer');
  const router = useRouter();
  const pathname = usePathname();
  const paddingBottom = pathname.startsWith('/goods-detail') ? 'pb-[80px]' : 'pb-[24px]';
  // 全局组件的 B 位跟随当前页面，C/D 沿用全局终端模板。
  const spmPage = useGlobalSpmTerminal(deviceType);
  // Data SPM DOM 打标：与各点击上报的坑位同源同值（查表无该坑位时 undefined，自然不打标）。
  const helpCenterDomSpm = useSpmDomAttr(
    getSpmPositionCode(spmPage, 'footer_help', 'open_help_center'),
  );
  const orderTrackingDomSpm = useSpmDomAttr(
    getSpmPositionCode(spmPage, 'footer_help', 'open_order_tracking'),
  );
  // 与 index.tsx SDSocialMediaClick 上报同一 FOOTER_SOCIAL_POSITION_WAP 查表；
  // hook 不能进循环，5 个社交入口逐码解析提到组件顶层。
  const resolveSocialSpmCnt = (btn: string) => {
    const spmPosition = FOOTER_SOCIAL_POSITION_WAP[btn];
    return spmPosition ? getSpmPositionCode(spmPage, 'footer_social', spmPosition) : undefined;
  };
  const socialDomSpmByTrackName = new Map<string, { 'data-spm': string }>(
    (
      [
        ['ins', useSpmDomAttr(resolveSocialSpmCnt('ins'))],
        ['fb', useSpmDomAttr(resolveSocialSpmCnt('fb'))],
        ['tiktok', useSpmDomAttr(resolveSocialSpmCnt('tiktok'))],
        ['twitter', useSpmDomAttr(resolveSocialSpmCnt('twitter'))],
        ['youtube', useSpmDomAttr(resolveSocialSpmCnt('youtube'))],
      ] as const
    ).flatMap(([trackName, domSpm]) => (domSpm ? [[trackName, domSpm] as const] : [])),
  );
  const isShowCouponSubscribe = !IS_US && isShowSubscribe;
  const isShowSubscribeUS = shouldShowUSFooterSubscribe(pathname);
  // navList 已由服务端从 init_wap.nav 中筛选 type=5，复用 PC 的纯配置解析逻辑。
  const legalLinks = getFooterLegalLinks(navList);
  const paymentModules = getFooterPaymentModules(navList, env.NEXT_PUBLIC_S3_URL);
  const copyrightModules = getFooterCopyrightModules(navList);
  const socialLinks = [
    {
      href: env.NEXT_PUBLIC_INS,
      label: t('zeeloolAtInstagram'),
      trackName: 'ins',
      Icon: InstagramOutlined,
    },
    {
      href: env.NEXT_PUBLIC_FB,
      label: t('zeeloolAtFacebook'),
      trackName: 'fb',
      Icon: FacebookOutlined,
    },
    {
      href: env.NEXT_PUBLIC_TT,
      label: t('zeeloolAtTikTok'),
      trackName: 'tiktok',
      Icon: TiktokOutlined,
    },
    {
      href: env.NEXT_PUBLIC_X,
      label: t('zeeloolAtTwitter'),
      trackName: 'twitter',
      Icon: TwitterOutlined,
    },
    {
      href: env.NEXT_PUBLIC_YTB,
      label: t('zeeloolAtYouTube'),
      trackName: 'youtube',
      Icon: YoutubeOutlined,
    },
  ].filter((item) => Boolean(item.href));

  return (
    <footer
      className={`Footer mobile flex w-full flex-col items-center bg-black px-[12px] ${paddingBottom}`}
    >
      {isShowSubscribeUS ? <Subscribe_US /> : null}
      {isShowCouponSubscribe ? (
        <SubscribeCoupon bottomSubscribeBoxTxt={bottomSubscribeBoxTxt} />
      ) : null}

      <section className="flex w-full flex-col items-center justify-center gap-[16px] py-[16px] font-br text-primary-white">
        <h2 className="w-full text-center font-bb text-[24px] leading-[28px]">{t('needHelp')}</h2>
        <div className="grid w-full grid-cols-2 gap-[48px] px-[12px]">
          <Link
            prefetch={false}
            href={env.NEXT_PUBLIC_HELP_CENTER_PATH}
            aria-label={t('mobile.helpCenter')}
            rel="noopener"
            className="flex flex-col items-center justify-center gap-[4px]"
            onClick={(event) => {
              event.preventDefault();
              SDBottomNavTabClick(
                'Need Help?',
                'Help Center',
                0,
                0,
                getSpmPositionCode(spmPage, 'footer_help', 'open_help_center'),
              );
              router.push(env.NEXT_PUBLIC_HELP_CENTER_PATH);
            }}
            {...helpCenterDomSpm}
          >
            <NextImage
              src={helpCenterIcon}
              alt={t('mobile.helpCenter')}
              className="size-[48px] text-primary-white"
              width={48}
              height={48}
              unoptimized
            />
            <span className="whitespace-nowrap font-bb text-[14px] leading-[17px] text-primary-white">
              {t('mobile.helpCenter')}
            </span>
          </Link>
          <Link
            prefetch={false}
            href="/track-order"
            aria-label={t('mobile.orderTracking')}
            className="flex flex-col items-center justify-center gap-[4px]"
            onClick={(event) => {
              event.preventDefault();
              SDBottomNavTabClick(
                'Need Help?',
                'Order tracking',
                0,
                1,
                getSpmPositionCode(spmPage, 'footer_help', 'open_order_tracking'),
              );
              router.push('/track-order');
            }}
            {...orderTrackingDomSpm}
          >
            <NextImage
              src={orderTrackingIcon}
              alt={t('mobile.orderTracking')}
              className="size-[48px] text-primary-white"
              width={48}
              height={48}
              unoptimized
            />
            <span className="whitespace-nowrap font-bb text-[14px] leading-[17px] text-primary-white">
              {t('mobile.orderTracking')}
            </span>
          </Link>
        </div>
      </section>

      <NavList navList={navList} />

      <div className="flex w-full flex-col gap-[8px]">
        <section className="flex w-full flex-col items-center justify-center gap-[12px] rounded-[6px] bg-[#282828] py-[20px]">
          <p className="font-br text-[13px] font-normal leading-none text-primary-white">
            {t('social.followUsOn')}
          </p>
          <div className="flex w-full items-center justify-center gap-[16px] py-[4px]">
            {socialLinks.map(({ href, label, trackName, Icon }) => (
              <Link
                key={trackName}
                prefetch={false}
                href={href}
                aria-label={label}
                target="_blank"
                rel="noopener nofollow noreferrer"
                onClick={() => SDSocialMediaClick(trackName)}
                {...socialDomSpmByTrackName.get(trackName)}
              >
                <Icon className="size-[24px] text-primary-white" />
              </Link>
            ))}
          </div>
          {env.NEXT_PUBLIC_WAP_SHOW_FB_GROUP === 'true' ? (
            <Link
              prefetch={false}
              className="relative block text-center"
              href={env.NEXT_PUBLIC_FB_GROUP}
              target="_blank"
              rel="noopener nofollow noreferrer"
            >
              <Image
                src={env.NEXT_PUBLIC_FB_GROUP_IMG}
                alt={t('social.joinGroup')}
                width={173}
                height={40}
              />
            </Link>
          ) : null}
        </section>

        {env.NEXT_PUBLIC_SHOW_DOWNLOAD_APP === 'true' ? <DownloadApp /> : null}

        {legalLinks.length > 0 ? (
          <ul className="flex w-full flex-wrap items-center justify-center gap-x-[12px] gap-y-[8px] py-[8px] font-br text-[12px] leading-[16px] text-primary-white">
            {legalLinks.map((legalLink, index) => (
              <Fragment key={legalLink.key}>
                {index > 0 ? <li aria-hidden="true">|</li> : null}
                <li className="min-w-0 break-words text-center">
                  <FooterLegalItem
                    item={legalLink}
                    onActivate={() => {
                      SDBottomNavTabClick(
                        legalLink.firstLevelTitle,
                        legalLink.label,
                        legalLink.firstLevelIndex,
                        legalLink.secondLevelIndex,
                      );
                    }}
                  />
                </li>
              </Fragment>
            ))}
          </ul>
        ) : null}

        {paymentModules.map((payment) => (
          <section
            key={payment.key}
            className="flex w-full flex-col items-center gap-[12px] rounded-[6px] bg-[#262626] px-[12px] py-[20px]"
          >
            {payment.title ? (
              <p className="max-w-full break-words text-center font-br text-[13px] font-normal leading-none text-primary-white">
                {payment.title}
              </p>
            ) : null}
            <div
              className="flex w-full flex-wrap items-center justify-center gap-[8px]"
              aria-hidden="true"
            >
              {payment.images.map((src, index) => (
                <Image
                  key={`${src}:${index}`}
                  src={src}
                  alt=""
                  width={36}
                  height={24}
                  unoptimized
                  className="h-[24px] w-[36px] shrink-0 rounded-[2px] object-contain"
                />
              ))}
            </div>
          </section>
        ))}

        {copyrightModules.map((copyright) => (
          <div
            key={copyright.key}
            className="flex w-full flex-col items-center justify-center gap-[8px] py-[8px]"
          >
            <p className="w-full break-words text-center font-hr text-[12px] font-normal leading-[16px] text-primary-white">
              {copyright.text}
            </p>
          </div>
        ))}
      </div>
    </footer>
  );
};

export default Footer;
